/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * Footer: Dark, minimal, industrial. Thick top border. Full brand portfolio listed.
 * SEO: All brand names with URLs rendered as crawlable anchor tags.
 */
import { Link } from "wouter";

const towingBrands = [
  { name: "Towing AI", color: "bg-amber-400" },
  { name: "US Tow Bid", color: "bg-amber-400", href: "https://www.ustowbid.com" },
  { name: "US Tow Command", color: "bg-cyan-400", href: "https://www.ustowcommand.com" },
  { name: "US Tow Grade", color: "bg-rose-400", href: "https://www.ustowgrade.com" },
  { name: "US Tow Jobs", color: "bg-emerald-400", href: "https://www.ustowjobs.com" },
  { name: "Iron Horse Tow Trucks", color: "bg-zinc-400", href: "https://indigo-aleda-56.tiiny.site" },
  { name: "US Tow Fleet", color: "bg-violet-400", href: "https://www.ustowfleet.com" },
  { name: "U.S. Tow Alliance", color: "bg-indigo-400", href: "https://ustowalliance.com" },
  { name: "Shift Track", color: "bg-sky-400", href: "https://www.shifttrack.online" },
  { name: "Fleet Command", color: "bg-blue-400", href: "https://fleetcommand.manus.space" },
  { name: "US Tow AI Connect", color: "bg-lime-400", href: "https://www.ustowaiconnect.com" },
];

const autoBrands = [
  { name: "AutoShop AI", color: "bg-red-400" },
  { name: "CONVINI", color: "bg-sky-400", href: "https://www.convinicar.online/" },
  { name: "WrenchLink", color: "bg-red-400", href: "https://tomato-gwenora-60.tiiny.site" },
];

const financeBrands = [
  { name: "Bridge Capital", color: "bg-yellow-400", href: "https://fuchsia-sallyanne-97.tiiny.site" },
  { name: "MCA Debt Solutions", color: "bg-orange-400", href: "https://mcaloanproblems.online" },
  { name: "Insurance Solutions", color: "bg-teal-400", href: "https://insurancesol-prqw5vtw.manus.space" },
  { name: "Recon AI", color: "bg-green-400", href: "https://reconai.manus.space" },
  { name: "Recon AI DIY Tool Kit", color: "bg-lime-400", href: "https://reconaitool.manus.space" },
];

const educationBrands = [
  { name: "AI Survival School", color: "bg-electric", href: "https://www.aisurvivalschool.online" },
  { name: "Peer Field Press", color: "bg-fuchsia-400", href: "https://www.peerfieldpress.com" },
];

const totalBrandCount =
  towingBrands.length + autoBrands.length + financeBrands.length + educationBrands.length;

function BrandItem({ brand }: { brand: { name: string; color: string; href?: string } }) {
  const inner = (
    <>
      <span className={`w-1.5 h-1.5 ${brand.color} shrink-0`} />
      <span className="text-steel-light text-sm hover:text-white transition-colors">
        {brand.name}
      </span>
    </>
  );

  if (brand.href) {
    return (
      <a
        href={brand.href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 py-2 min-h-[44px]"
        title={brand.name}
      >
        {inner}
      </a>
    );
  }

  return <div className="flex items-center gap-2 py-2 min-h-[44px]">{inner}</div>;
}

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t-[3px] border-electric/20 bg-navy-deep py-12" role="contentinfo">
      <div className="container">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <a href="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 border-[3px] border-electric rounded-sm flex items-center justify-center bg-electric/10">
                <span className="font-heading font-bold text-electric text-sm">BC</span>
              </div>
              <span className="font-heading font-bold text-lg text-white">
                BLUE COLLAR <span className="text-electric">AI</span>
              </span>
            </a>
            <p className="text-steel text-sm leading-relaxed max-w-xs mb-4">
              Smart tech for hard work. AI consulting and solutions for blue collar
              businesses that keep America running.
            </p>
            <p className="font-mono text-xs text-steel">
              {totalBrandCount} brands and growing.
            </p>
          </div>

          {/* Towing & Fleet */}
          <nav aria-label="Towing and Fleet brands">
            <h4 className="font-mono text-[10px] text-steel tracking-[0.3em] uppercase mb-4">
              Towing & Fleet
            </h4>
            <div className="flex flex-col gap-0.5">
              {towingBrands.map((brand) => (
                <BrandItem key={brand.name} brand={brand} />
              ))}
            </div>
          </nav>

          {/* Auto & Service + Finance */}
          <nav aria-label="Auto, Service, and Finance brands">
            <h4 className="font-mono text-[10px] text-steel tracking-[0.3em] uppercase mb-4">
              Auto & Service
            </h4>
            <div className="flex flex-col gap-0.5">
              {autoBrands.map((brand) => (
                <BrandItem key={brand.name} brand={brand} />
              ))}
            </div>

            <h4 className="font-mono text-[10px] text-steel tracking-[0.3em] uppercase mb-4 mt-8">
              Finance & Protection
            </h4>
            <div className="flex flex-col gap-0.5">
              {financeBrands.map((brand) => (
                <BrandItem key={brand.name} brand={brand} />
              ))}
            </div>

            <h4 className="font-mono text-[10px] text-steel tracking-[0.3em] uppercase mb-4 mt-8">
              Books & Education
            </h4>
            <div className="flex flex-col gap-0.5">
              {educationBrands.map((brand) => (
                <BrandItem key={brand.name} brand={brand} />
              ))}
            </div>
          </nav>

          {/* Quick Links + Partners */}
          <nav aria-label="Quick links">
            <h4 className="font-mono text-[10px] text-steel tracking-[0.3em] uppercase mb-4">
              Quick Links
            </h4>
            <div className="flex flex-col gap-0.5">
              {[
                { label: "Partners", href: "#subsidiaries" },
                { label: "Services", href: "#services" },
                { label: "Our Brands", href: "#subsidiaries" },
                { label: "About Us", href: "#about" },
                { label: "Contact", href: "#contact" },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="flex items-center text-steel-light text-sm hover:text-white transition-colors py-2 min-h-[44px]"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </nav>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-xs text-steel text-center sm:text-left">
            &copy; {currentYear} Blue Collar AI, Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link
              href="/privacy-policy"
              className="font-mono text-xs text-steel hover:text-electric transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              className="font-mono text-xs text-steel hover:text-electric transition-colors"
            >
              Terms &amp; Conditions
            </Link>
            <p className="font-mono text-xs text-steel">
              Columbus, OH &mdash; Serving World Wide
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
